import { useQuery } from "@apollo/client";
import { withJsonFormsControlProps } from "@jsonforms/react";
import { debounce } from "lodash";
import { Button, Checkbox, LoadingSpinner, Notification, Tag } from "pepsico-ds";
import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { GlobalConfigContext } from "../../../context/GlobalConfigContext";
import { listCampaignsLeaderboardIds } from "../../../graphql/queries/getCampaignListing";
import { getLeaderboardsList } from "../../../graphql/queries/getLeaderboardsList";
import CreateLeaderboardsModal from "../../../pages/leaderboards/CreateLeaderboardsModal";
import { getDateWithFormat } from "../../../utils/commons";
import FilterLeaderboards from "../../leaderboards/filterLeaderboards/FilterLeaderboards";
import "./LeaderboardSelect.scss";

const LeaderboardSelect = (props) => {
  const { handleChange, path } = props;
  const { programTimezone } = useContext(GlobalConfigContext);
  const [showNotification, setShowNotification] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedLeaderboards, setSelectedLeaderboards] = useState([]);
  const [showCreateLeaderboardModal, setShowCreateLeaderboardModal] =
    useState(false);
  const filters = useRef({ title: "", startsAt: "", endsAt: "" });

  const { data: campaignsData, loading: campaignsLoading } = useQuery(
    listCampaignsLeaderboardIds
  );

  const {
    data: listData,
    refetch,
    loading: leaderboardsLoading,
  } = useQuery(getLeaderboardsList, {
    variables: { leaderboardStatus: "ACTIVE" },
  });

  // Extract all leaderboard IDs that are already associated with campaigns using useMemo
  const campaignLeaderboardIds = useMemo(() => {
    return campaignsData?.listCampaigns?.length
      ? campaignsData.listCampaigns
          .filter((campaign) => campaign.leaderboards)
          .flatMap((campaign) => campaign.leaderboards)
      : [];
  }, [campaignsData]);

  // Filter out leaderboards that are already associated with campaigns using useMemo
  const leaderboardList = useMemo(() => {
    return !campaignsLoading && listData?.listLeaderboards?.items?.length
      ? listData?.listLeaderboards?.items.filter(
          (leaderboard) => !campaignLeaderboardIds.includes(leaderboard.id)
        )
      : [];
  }, [listData, campaignLeaderboardIds, campaignsLoading]);

  // const leaderboardList = listData?.listLeaderboards?.items || [];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onChangeFilters = useCallback(
    debounce(() => {
      const payload = {
        title: "",
        startsAt: "",
        endsAt: "",
        leaderboardStatus: "ACTIVE",
      };

      if (filters.current.title) {
        payload.title = filters.current.title;
      }
      if (filters.current.startsAt) {
        payload.startsAt = filters.current.startsAt;
      }
      if (filters.current.endsAt) {
        payload.endsAt = filters.current.endsAt;
      }
      refetch({ ...payload });
    }, 500),
    []
  );

  const onSelected = (leaderboard) => {
    const isExists = selectedIds.includes(leaderboard.id);
    let finalIds = [];
    let finalLeaderboards = [];
    if (isExists) {
      finalIds = selectedIds.filter((item) => item !== leaderboard.id);
      finalLeaderboards = selectedLeaderboards.filter(
        (item) => item.id !== leaderboard.id
      );
    } else {
      finalIds = [...selectedIds, leaderboard.id];
      finalLeaderboards = [...selectedLeaderboards, { ...leaderboard, rewards: [] }];
    }
    setSelectedIds(finalIds);
    setSelectedLeaderboards(finalLeaderboards);

    try {
      handleChange(path, finalLeaderboards);
    } catch (error) {
      console.error("Error in handleChange:", error);
    }
  };

  const toggleCreateLeaderboardModal = (showToast = true) => {
    setShowCreateLeaderboardModal(false);
    refetch();
    setShowNotification(showToast);
    setTimeout(() => {
      setShowNotification(false);
    }, 5000);
  };

  return (
    <div className="add-leaderboard-select-wrap">
      <div className="display-flex align-center mb-3">
        <h4 className="font-semibold mr-3">Add Leaderboard</h4>
        {/* <h4 className="font-light">Campaign Duration: 12/22/2024-01/11/2025</h4> */}
      </div>
      {showNotification && (
        <Notification type="success" title="New Leaderboard Created!" text="" />
      )}
      <div className="my-3 select-count-box">
        <p className="font-xsmall-bold">
          {selectedLeaderboards.length} Leaderboard(s) Selected
        </p>
        {selectedLeaderboards.length > 0 && (
          <div className="leaderboards-tags mt-3">
            {selectedLeaderboards.map((leaderboard) => (
              <Tag
                key={leaderboard.id}
                text={leaderboard.title}
                isRemovable={true}
                isCopyable={false}
                color="Auxiliary-600"
                onRemove={() => onSelected(leaderboard)}
                className="tag-label" // Ensure we have a consistent class for the span
              />
            ))}
          </div>
        )}
      </div>
      <div className="mt-3 display-flex">
        <FilterLeaderboards
          filterRef={filters}
          onChangeFilters={onChangeFilters}
          showFields={{
            search: true,
            dates: true,
          }}
        />
        <Button
          text="Create New Leaderboard"
          iconLeading="add"
          variant="tertiary"
          size="medium"
          onClick={() => {
            setShowCreateLeaderboardModal(true);
          }}
        />
      </div>
      <div>
        <table className="leaderboards-list-table">
          <thead>
            <tr>
              <th></th>
              <th>TITLE</th>
              <th>DATE RANGE</th>
              <th>APPROVAL METHOD</th>
            </tr>
          </thead>
          <tbody>
            {leaderboardList.length === 0 && (
              <tr>
                <td colSpan="4" className="font-bold text-center">
                  No leaderboards found
                </td>
              </tr>
            )}
            {(leaderboardsLoading || campaignsLoading) && (
              <tr>
                <td colSpan="4" className="text-center">
                  <LoadingSpinner
                    size={{
                      height: "40px",
                      width: "40px",
                    }}
                  />
                </td>
              </tr>
            )}
            {leaderboardList?.map((leaderboard) => (
              <tr key={leaderboard.id}>
                <td>
                  <Checkbox
                    label=""
                    size="small"
                    text=""
                    checked={selectedIds.includes(leaderboard.id)}
                    onUpdate={() => onSelected(leaderboard)}
                  />
                </td>
                <td>
                  <p className="wrap-leaderboard-title" title={leaderboard.title}>
                    {leaderboard.title}
                  </p>
                </td>
                <td>
                  {getDateWithFormat(
                    leaderboard.startsAt,
                    programTimezone,
                    "MM/DD/YYYY"
                  )}{" "}
                  -{" "}
                  {getDateWithFormat(
                    leaderboard.endsAt,
                    programTimezone,
                    "MM/DD/YYYY"
                  )}
                </td>
                <td>{leaderboard.awardApproval}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <CreateLeaderboardsModal
        showModal={showCreateLeaderboardModal}
        toggleCreateLeaderboardModal={toggleCreateLeaderboardModal}
        refetch={() => {}} // We handle refetch in the toggle function now
        selectedLeaderboard={null}
        setSelectedLeaderboard={() => null}
        createShowToast={false}
      />
    </div>
  );
};

export const LeaderboardSelectControl = withJsonFormsControlProps(LeaderboardSelect);
